// stores/useStockStore.js - REFACTORED FOR SECURITY
// Removed all localStorage token/connection logic
// Use BrokerIntegrationContext for broker state instead

import { create } from 'zustand';

// Initial state for stocks to reset to when removing
const initialStocks = [
  { symbol: 'SPX', value: 0, type: null, hedge: false, status: '', quanlity: 0 },
  { symbol: 'SPY', value: 0, type: null, hedge: false, status: '', quanlity: 0 },
  { symbol: 'QQQ', value: 0, type: null, hedge: false, status: '', quanlity: 0 },
];

// Load saved stocks from localStorage if available
const loadSavedStocks = () => {
  try {
    const saved = localStorage.getItem('strategyCenterData');
    if (saved) {
      const parsed = JSON.parse(saved);
      console.log('📖 Loaded saved Strategy Center data:', parsed);
      return parsed;
    }
  } catch (error) {
    console.error('❌ Error loading saved Strategy Center data:', error);
  }
  return [...initialStocks];
};

/**
 * Stock Store - UI-only state management for dashboard stocks
 * 
 * SECURITY NOTE: This store NO LONGER handles:
 * - Broker connection state (use BrokerIntegrationContext)
 * - Token storage (use SecureBrokerTokenService)
 * - Direct localStorage access for sensitive data
 * 
 * Use BrokerIntegrationContext.brokerIntegrations to check connection status
 */
const useStockStore = create((set) => ({
  // Dashboard stock state (UI only)
  stocks: loadSavedStocks(), // Load saved data or use initial state
  editStock: null,
  showAddModal: false,
  showRiskDisclosure: false,
  showDisclaimerModal: false,
  showAllSymbolsSelectedPopup: false,

  // Broker accounts state (derived from BrokerIntegrationContext)
  brokerAccounts: [],
  status: 'idle', // 'idle' | 'loading' | 'error' | 'success'

  // Actions for stocks
  setEditStock: (stock) => {
    console.log('🛠 setEditStock called with:', stock);
    set({ editStock: stock });
  },

  setStocks: (stocks) => set({ stocks }),

  updateStockValue: (value, symbol) => set((state) => {
    const existingStockIndex = state.stocks.findIndex(stock => stock.symbol === symbol);
    if (existingStockIndex === -1) return state;

    const updatedStock = {
      ...state.stocks[existingStockIndex],
      value: parseFloat(value)
    };

    const newStocks = [...state.stocks];
    newStocks[existingStockIndex] = updatedStock;

    // Auto-save to localStorage
    try {
      localStorage.setItem('strategyCenterData', JSON.stringify(newStocks));
      console.log('💾 Auto-saved Strategy Center data (updateStockValue)');
    } catch (error) {
      console.error('❌ Error auto-saving Strategy Center data:', error);
    }

    return { stocks: newStocks };
  }),

  updateStockType: (symbol, type) => set((state) => {
    const newStocks = state.stocks.map(stock =>
      stock.symbol === symbol ? { ...stock, type } : stock
    );

    // Auto-save to localStorage
    try {
      localStorage.setItem('strategyCenterData', JSON.stringify(newStocks));
      console.log('💾 Auto-saved Strategy Center data (updateStockType)');
    } catch (error) {
      console.error('❌ Error auto-saving Strategy Center data:', error);
    }

    return { stocks: newStocks };
  }),

  updateStockStatus: (symbol, newStatus, newHedge) => set((state) => {
    const newStocks = state.stocks.map(stock =>
      stock.symbol === symbol ? { ...stock, status: newStatus, hedge: newHedge } : stock
    );

    // Auto-save to localStorage
    try {
      localStorage.setItem('strategyCenterData', JSON.stringify(newStocks));
      console.log('💾 Auto-saved Strategy Center data (updateStockStatus)');
    } catch (error) {
      console.error('❌ Error auto-saving Strategy Center data:', error);
    }

    return { stocks: newStocks };
  }),

  // Update stock quanlity
  updateStockQuanlity: (symbol, quanlity) => set((state) => {
    const newStocks = state.stocks.map(stock =>
      stock.symbol === symbol ? { ...stock, quanlity } : stock
    );

    // Auto-save to localStorage
    try {
      localStorage.setItem('strategyCenterData', JSON.stringify(newStocks));
      console.log('💾 Auto-saved Strategy Center data (updateStockQuanlity)');
    } catch (error) {
      console.error('❌ Error auto-saving Strategy Center data:', error);
    }

    return { stocks: newStocks };
  }),

  // Update multiple stocks at once based on form data
  updateStocksFromForm: (stocksData) => set((state) => {
    const updatedStocks = [...state.stocks];

    stocksData.forEach(stockData => {
      const index = updatedStocks.findIndex(s => s.symbol === stockData.symbol);
      if (index !== -1) {
        updatedStocks[index] = {
          ...updatedStocks[index],
          value: stockData.quanlity, // Use quanlity as value to display
          type: stockData.type,
          hedge: stockData.hedge,
          status: stockData.status,
          quanlity: stockData.quanlity, // Use quanlity instead of quantity
        };
      }
    });

    // Auto-save to localStorage
    try {
      localStorage.setItem('strategyCenterData', JSON.stringify(updatedStocks));
      console.log('💾 Auto-saved Strategy Center data to localStorage');
    } catch (error) {
      console.error('❌ Error auto-saving Strategy Center data:', error);
    }

    return {
      stocks: updatedStocks,
      showRiskDisclosure: false,
      showAddModal: false
    };
  }),

  // Reset a stock to its initial state
  resetStock: (symbol) => set((state) => {
    const initialStock = initialStocks.find(stock => stock.symbol === symbol);
    if (!initialStock) return state;

    const newStocks = state.stocks.map(stock =>
      stock.symbol === symbol ? { ...initialStock } : stock
    );

    return { stocks: newStocks };
  }),

  // Reset all stocks to initial state
  resetAllStocks: () => set((state) => {
    const newStocks = [...initialStocks];
    
    // Auto-save to localStorage after reset all
    try {
      localStorage.setItem('strategyCenterData', JSON.stringify(newStocks));
      console.log('💾 Auto-saved Strategy Center data after reset all (resetAllStocks)');
    } catch (error) {
      console.error('❌ Error auto-saving Strategy Center data after reset all:', error);
    }
    
    return { stocks: newStocks };
  }),

  // Modal controls
  setShowAddModal: (show) => {
    console.log('🔧 useStockStore - setShowAddModal called with:', show);
    set({ showAddModal: show });
  },
  setShowRiskDisclosure: (show) => {
    console.log('🔧 useStockStore - setShowRiskDisclosure called with:', show);
    set({ showRiskDisclosure: show });
  },
  setShowDisclaimerModal: (show) => {
    console.log('🔧 useStockStore - setShowDisclaimerModal called with:', show);
    set({ showDisclaimerModal: show });
  },
  setShowAllSymbolsSelectedPopup: (show) => {
    console.log('🔧 useStockStore - setShowAllSymbolsSelectedPopup called with:', show);
    set({ showAllSymbolsSelectedPopup: show });
  },

  // Broker accounts actions (UI state only - actual data from BrokerIntegrationContext)
  setBrokerAccounts: (accounts) => set({ brokerAccounts: accounts }),
  setStatus: (newStatus) => set({ status: newStatus }),

  // REMOVED: All localStorage connection state management
  // REMOVED: toggleConnection, forceConnect, forceDisconnect, disconnect
  // USE INSTEAD: BrokerIntegrationContext.brokerIntegrations to check connection status
  
  // Fetch broker accounts (should use SecureBrokerTokenService)
  fetchBrokerAccounts: async () => {
    set({ status: 'loading' });
    try {
      // This should now use SecureBrokerTokenService instead of direct API calls
      console.warn('fetchBrokerAccounts: Use SecureBrokerTokenService for broker API calls');
      set({ status: 'success' });
    } catch (error) {
      set({ status: 'error' });
      console.error('Failed to fetch broker accounts:', error);
    }
  },
}));

export default useStockStore;